/**
 * i18n — přepínatelná lokalizace dashboardu (CZ default + EN/IT/CVM).
 *
 * Jazyk: localStorage 'acm_lang' → default 'cs'. Přepíná se přes setLang()
 * (LanguageSwitcher v shellu). Bez extra detektoru — minimální deps.
 *
 * Migrace stringů inkrementálně: komponenty volají useTranslation() → t('klíč').
 * Nepřeložené klíče padají na fallback 'cs' (origin jazyk), takže částečná
 * migrace nikdy nerozbije UI — jen ukáže český text dokud se klíč nedoplní.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import cs from '../locales/cs.json';
import en from '../locales/en.json';
import it from '../locales/it.json';
import cvm from '../locales/cvm.json';

const SAVED = (() => {
  try {
    return localStorage.getItem('acm_lang');
  } catch {
    return null;
  }
})();

export const SUPPORTED = ['cs', 'en', 'it', 'cvm'] as const;
export type Lang = (typeof SUPPORTED)[number];

export function isSupportedLang(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED as readonly string[]).includes(value);
}

export function normalizeLang(value: unknown): Lang {
  if (typeof value !== 'string') return 'cs';
  const lower = value.toLowerCase();
  if (isSupportedLang(lower)) return lower;
  const base = lower.split(/[-_]/)[0];
  return isSupportedLang(base) ? base : 'cs';
}

const initial: Lang = isSupportedLang(SAVED) ? SAVED : 'cs'; // default CZ

void i18n.use(initReactI18next).init({
  resources: {
    cs: { translation: cs },
    en: { translation: en },
    it: { translation: it },
    cvm: { translation: cvm },
  },
  lng: initial,
  fallbackLng: 'cs',
  interpolation: { escapeValue: false }, // React už escapuje
  returnNull: false,
});

export function setLang(l: Lang): void {
  try {
    localStorage.setItem('acm_lang', l);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(l);
}

export default i18n;
